import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, KeyRound, Users, Wallet, X, Activity, ChevronDown, Download, FileText, Search } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { downloadCsv, downloadPdf } from "../../lib/download";

const fmtEventDate = (d) => {
  if (!d) return "No date";
  const dt = new Date(d);
  if (isNaN(dt)) return "No date";
  const hasTime = /T\d/.test(d);
  return dt.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", ...(hasTime ? { hour: "2-digit", minute: "2-digit" } : {}) });
};

export default function StaffTab() {
  const [data, setData] = useState(null);
  const [takings, setTakings] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", event_ids: [] });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // staff being edited (event assignment)
  const [feed, setFeed] = useState([]);
  const [showEvents, setShowEvents] = useState({});
  const [feedEvent, setFeedEvent] = useState("all");
  const [cashups, setCashups] = useState([]);

  const loadCashups = () => api.get("/business/staff/cashups").then(({ data }) => setCashups(data.cashups || [])).catch(() => {});

  const cashUp = async (s) => {
    const t = takingsFor(s.id);
    const owed = t ? t.total : 0;
    if (!window.confirm(`Cash up ${s.name}? This records £${owed.toFixed(2)} as collected and resets their running total.`)) return;
    const note = window.prompt("Optional note (e.g. 'float £50 returned'):") || "";
    try {
      const { data } = await api.post(`/business/staff/${s.id}/cashup`, { note });
      toast({ title: "Cashed up", description: `Collected £${data.total.toFixed(2)} from ${s.name} (£${data.cash.toFixed(2)} cash · £${data.card.toFixed(2)} card).` });
      load(); loadFeed(); loadCashups();
    } catch (e) { toast({ title: "Couldn't cash up", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const exportReconciliation = async () => {
    try { await downloadCsv("/business/staff/reconciliation.csv", "staff-reconciliation.csv"); }
    catch { toast({ title: "Export failed", variant: "destructive" }); }
  };

  const load = () => {
    api.get("/business/staff").then(({ data }) => setData(data)).catch(() => setData({ staff: [], events: [] }));
    api.get("/business/staff/takings").then(({ data }) => setTakings(data.staff || [])).catch(() => {});
  };
  const loadFeed = () => api.get("/business/door-feed?limit=30").then(({ data }) => setFeed(data.feed || [])).catch(() => {});
  useEffect(() => {
    load(); loadFeed(); loadCashups();
    const t = setInterval(() => { loadFeed(); api.get("/business/staff/takings").then(({ data }) => setTakings(data.staff || [])).catch(() => {}); }, 12000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const takingsFor = (sid) => takings.find((t) => t.staff_id === sid);

  const addStaff = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) { toast({ title: "Name, email and a 6+ char password are required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/business/staff", { ...form, name: form.name.trim(), email: form.email.trim() });
      toast({ title: "Staff login created", description: `${form.email} can now sign in at the door.` });
      setShowAdd(false); setForm({ name: "", email: "", password: "", event_ids: [] }); load();
    } catch (e) { toast({ title: "Couldn't create", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const saveEvents = async (sid, ids) => {
    await api.patch(`/business/staff/${sid}`, { event_ids: ids });
    toast({ title: "Events updated" }); setEditing(null); load();
  };
  const resetPw = async (s) => {
    const pw = window.prompt(`New password for ${s.email} (min 6 chars):`);
    if (!pw) return;
    try { await api.post(`/business/staff/${s.id}/password`, { password: pw }); toast({ title: "Password reset" }); }
    catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" }); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}'s login?`)) return;
    await api.delete(`/business/staff/${s.id}`); toast({ title: "Login removed" }); load();
  };
  const downloadRoster = async (s) => {
    try { await downloadPdf(`/business/staff/${s.id}/roster.pdf`, `${(s.name || "staff").toLowerCase().replace(/\s+/g, "-")}-roster.pdf`); }
    catch { toast({ title: "Couldn't generate roster", variant: "destructive" }); }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={28} /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[#5a6b80] text-sm max-w-2xl">Create logins for door staff, assign them to specific events, and track how much each person has taken so you can reconcile cash. Staff can scan tickets and sell on the door — but can't edit events or settings.</p>
        <button onClick={() => setShowAdd(true)} data-testid="add-staff-btn" className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 shrink-0"><Plus size={16} /> Add staff login</button>
        {cashups.length > 0 && <button onClick={exportReconciliation} data-testid="reconciliation-csv" className="gp-btn-outline rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2 shrink-0"><Download size={15} /> Reconciliation CSV</button>}
      </div>

      {data.staff.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-[#8a97a8]" data-testid="staff-empty"><Users className="mx-auto mb-3 text-[#c7d2e0]" size={32} />No door staff yet. Add your first login to get started.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.staff.map((s) => {
            const t = takingsFor(s.id);
            return (
              <div key={s.id} className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid={`staff-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-[#16243b]">{s.name}</p>
                    <p className="text-sm text-[#8a97a8]">{s.email}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => downloadRoster(s)} data-testid={`staff-roster-${s.id}`} title="Print roster (PDF)" className="p-2 rounded-lg text-[#8a97a8] hover:bg-gray-50 hover:text-[#16243b]"><FileText size={15} /></button>
                    <button onClick={() => resetPw(s)} data-testid={`staff-reset-${s.id}`} title="Reset password" className="p-2 rounded-lg text-[#8a97a8] hover:bg-gray-50 hover:text-[#16243b]"><KeyRound size={15} /></button>
                    <button onClick={() => remove(s)} data-testid={`staff-remove-${s.id}`} title="Remove" className="p-2 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-3 rounded-xl bg-[#f6f8fb] px-3 py-2.5" data-testid={`staff-takings-${s.id}`}>
                  <div className="flex items-center gap-2">
                    <Wallet size={16} className="text-[#2ec9c9]" />
                    {t ? <span className="text-sm text-[#16243b]"><strong>£{t.total.toFixed(2)}</strong> taken · Cash £{t.cash.toFixed(2)} · Card £{t.card.toFixed(2)} · {t.tickets} sold · {t.comps} comp(s)</span>
                       : <span className="text-sm text-[#8a97a8]">No sales yet</span>}
                    {t && t.events && t.events.length > 0 && (
                      <button onClick={() => setShowEvents({ ...showEvents, [s.id]: !showEvents[s.id] })} data-testid={`staff-by-event-${s.id}`} className="ml-auto text-xs text-[#1fb0b0] font-medium flex items-center gap-0.5">By event <ChevronDown size={13} className={`transition-transform ${showEvents[s.id] ? "rotate-180" : ""}`} /></button>
                    )}
                  </div>
                  {t && showEvents[s.id] && (
                    <div className="mt-2 pt-2 border-t border-gray-200 space-y-1" data-testid={`staff-event-breakdown-${s.id}`}>
                      {t.events.map((e) => (
                        <div key={e.title} className="flex items-center justify-between text-xs">
                          <span className="text-[#5a6b80] truncate mr-2">{e.title}</span>
                          <span className="text-[#16243b] shrink-0">£{(e.cash + e.card).toFixed(2)} <span className="text-[#8a97a8]">(£{e.cash.toFixed(2)} cash · £{e.card.toFixed(2)} card · {e.tickets} sold{e.comps ? ` · ${e.comps} comp` : ""})</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                  {t && t.total > 0 && (
                    <button onClick={() => cashUp(s)} data-testid={`staff-cashup-${s.id}`} className="mt-2 w-full rounded-lg py-2 text-xs font-semibold border border-[#16243b] text-[#16243b] hover:bg-[#16243b] hover:text-white transition-colors flex items-center justify-center gap-1.5"><Wallet size={13} /> Cash up · collect £{t.total.toFixed(2)}</button>
                  )}
                </div>
                <div className="mt-3">
                  <p className="text-xs font-medium text-[#8a97a8] mb-1.5">Assigned events ({s.events.length})</p>
                  {editing === s.id ? (
                    <EventPicker events={data.events} value={s.event_ids} onCancel={() => setEditing(null)} onSave={(ids) => saveEvents(s.id, ids)} />
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {s.events.map((e) => <span key={e.id} className="text-xs bg-[#eefafa] text-[#1fb0b0] rounded-full px-2.5 py-1" title={e.venue_name || ""}>{e.title} · {fmtEventDate(e.event_date)}{e.venue_name ? ` · ${e.venue_name}` : ""}</span>)}
                      {s.events.length === 0 && <span className="text-xs text-[#c0392b]">None assigned</span>}
                      <button onClick={() => setEditing(s.id)} data-testid={`staff-edit-events-${s.id}`} className="text-xs text-[#1fb0b0] font-medium hover:underline ml-1">Edit</button>
                    </div>
                  )}
                </div>
                {cashups.filter((c) => c.staff_id === s.id).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`cashup-history-${s.id}`}>
                    <p className="text-xs font-medium text-[#8a97a8] mb-1.5">Cash-up history</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {cashups.filter((c) => c.staff_id === s.id).slice(0, 8).map((c) => (
                        <div key={c.id} className="text-xs flex items-start justify-between gap-2">
                          <span className="text-[#5a6b80]">{new Date(c.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{c.note ? <em className="text-[#8a97a8]"> · {c.note}</em> : ""}</span>
                          <span className="text-[#16243b] font-medium shrink-0">£{c.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {feed.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="door-feed">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2ec9c9] opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2ec9c9]" /></span>
            <h3 className="font-semibold text-[#16243b] flex items-center gap-1.5"><Activity size={16} /> Live door sales</h3>
            <select value={feedEvent} onChange={(e) => setFeedEvent(e.target.value)} data-testid="feed-event-filter" className="ml-auto text-xs rounded-lg border border-gray-200 px-2 py-1 outline-none focus:border-[#2ec9c9]">
              <option value="all">All events</option>
              {[...new Set(feed.map((f) => f.product_title).filter(Boolean))].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {feed.filter((f) => feedEvent === "all" || f.product_title === feedEvent).map((f) => (
              <div key={f.code} className="flex items-center justify-between gap-3 rounded-lg bg-[#f9fbfd] px-3 py-2 text-sm border border-gray-100" data-testid={`feed-row-${f.code}`}>
                <div className="min-w-0">
                  <span className="font-medium text-[#16243b]">{f.sold_by_name || "Staff"}</span>
                  <span className="text-[#8a97a8]"> · {f.product_title} · {f.tier_name}</span>
                  {f.is_comp && f.comp_reason && <span className="block text-xs text-amber-600 italic truncate">comp: {f.comp_reason}</span>}
                </div>
                <div className="text-right shrink-0">
                  {f.is_comp
                    ? <span className="text-xs font-semibold text-amber-600">COMP</span>
                    : <span className="text-sm font-semibold text-[#16243b]">£{Number(f.amount_paid || 0).toFixed(2)} <span className={`text-xs font-normal ${f.pay_method === "card" ? "text-[#1fb0b0]" : "text-[#5a6b80]"}`}>{(f.pay_method || "cash")}</span></span>}
                  <p className="text-[10px] text-[#8a97a8]">{f.created_at ? new Date(f.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-staff-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-[#16243b] text-lg">Add staff login</h3><button onClick={() => setShowAdd(false)}><X size={18} className="text-[#8a97a8]" /></button></div>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" placeholder="Full name" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" type="email" placeholder="Email (their login)" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" type="text" placeholder="Password (min 6 chars — share it with them)" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <div>
                <p className="text-xs font-medium text-[#8a97a8] mb-1.5">Assign events</p>
                <EventChooser
                  events={data.events}
                  value={form.event_ids}
                  onChange={(ids) => setForm({ ...form, event_ids: ids })}
                  idPrefix="staff-event-opt"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAdd(false)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
              <button onClick={addStaff} disabled={busy} data-testid="staff-create-confirm" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 flex items-center gap-2">{busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EventPicker({ events, value, onCancel, onSave }) {
  const [ids, setIds] = useState(value || []);
  return (
    <div className="border border-gray-100 rounded-xl p-2">
      <EventChooser events={events} value={ids} onChange={setIds} maxHeight="max-h-40" idPrefix="staff-edit-opt" />
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={onCancel} className="text-xs text-[#8a97a8] px-3 py-1.5">Cancel</button>
        <button onClick={() => onSave(ids)} data-testid="staff-events-save" className="text-xs gp-btn-teal rounded-lg px-4 py-1.5 font-medium">Save</button>
      </div>
    </div>
  );
}

function EventChooser({ events, value, onChange, maxHeight = "max-h-48", idPrefix }) {
  const [venue, setVenue] = useState("all");
  const [q, setQ] = useState("");
  const venues = [...new Set((events || []).map((e) => e.venue_name).filter(Boolean))].sort();
  const query = q.trim().toLowerCase();
  const shown = (events || []).filter((e) =>
    (venue === "all" || e.venue_name === venue) &&
    (!query || (e.title || "").toLowerCase().includes(query))
  );
  const toggle = (id, on) => onChange(on ? [...value, id] : value.filter((x) => x !== id));
  const shownIds = shown.map((e) => e.id);
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => value.includes(id));
  const toggleAllShown = () => {
    if (allShownSelected) onChange(value.filter((id) => !shownIds.includes(id)));
    else onChange([...new Set([...value, ...shownIds])]);
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <div className="relative flex-1 min-w-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a97a8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="staff-event-search" placeholder="Search events…"
            className="w-full text-sm rounded-lg border border-gray-200 pl-8 pr-3 py-2 outline-none focus:border-[#2ec9c9]" />
        </div>
        {venues.length > 1 && (
          <select value={venue} onChange={(e) => setVenue(e.target.value)} data-testid="staff-venue-filter"
            className="text-sm rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-[#2ec9c9] shrink-0 max-w-[45%]">
            <option value="all">All venues</option>
            {venues.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
      </div>
      {shown.length > 0 && (
        <button type="button" onClick={toggleAllShown} data-testid="staff-select-all-shown"
          className="text-xs text-[#1fb0b0] font-medium hover:underline mb-1.5">
          {allShownSelected ? "Clear" : "Select all"} {venue !== "all" ? `at ${venue}` : query ? "matching" : "shown"} ({shown.length})
        </button>
      )}
      <div className={`${maxHeight} overflow-y-auto border border-gray-100 rounded-xl p-2 space-y-1`}>
        {shown.map((e) => (
          <label key={e.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={value.includes(e.id)} data-testid={`${idPrefix}-${e.id}`}
              onChange={(ev) => toggle(e.id, ev.target.checked)} className="w-4 h-4 mt-0.5 accent-[#2ec9c9] shrink-0" />
            <span className="text-sm text-[#16243b] leading-tight">{e.title}
              <span className="block text-xs text-[#8a97a8]">{fmtEventDate(e.event_date)}{e.venue_name ? ` · ${e.venue_name}` : ""}</span>
            </span>
          </label>
        ))}
        {shown.length === 0 && <p className="text-sm text-[#8a97a8] px-2 py-3">{events.length === 0 ? "Create an event first." : "No events match."}</p>}
      </div>
    </div>
  );
}
